import React, { useState, useEffect, useRef, useContext } from 'react'
import styles from './FilePreview.module.scss';
import type { FileRef } from '../WorkspaceContext';
import api from '../../../api';
import { useParams } from 'react-router-dom';
import { getFileRefIcon } from '../../../shared/utils/getFileIconAndExtensionHelper';
import SoundIcon from './SoundIcon';
import { createPortal } from 'react-dom';
import PlayIcon from './PlayIcon';
import PauseIcon from './PauseIcon';
import { WorkspaceContext } from '../WorkspaceContext';
import VolumeIcon from './VolumeIcon';
import FullScreenIcon from './FullScreenIcon';



type FilePreviewProps = {
    id: string;
    name: string;
    mime: string;
    fileExtension: string
};



const FilePreview = ({ id, name, mime, fileExtension }: FilePreviewProps) => {

    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { activeFile } = ctx;

    const [mediaPortalTarget, setMediaPortalTarget] = useState<HTMLElement | null>(null);
    const [videoPortalTarget, setVideoPortalTarget] = useState<HTMLElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [videoPlay, setVideoPlay] = useState(false);
    // const videoTimeRef = useRef<number | null>(null);
    // const sliderRef = useRef<HTMLDivElement | null>(null);
    const [videoPercentage, setVideoPercentage] = useState(0);



    const VideoPlayClick = async () => {
        const video = videoRef.current;
        if (!video) return;

        try {
            if (video.paused) {
                await video.play();
                setVideoPlay(true);
            } else {
                video.pause();
                setVideoPlay(false);
            }
        } catch (err) {

            setVideoPlay(false);
        }
    };



    const updateVideoProgress = () => {
        const video = videoRef.current;
        if (!video || !video.duration) return;

        const percent = (video.currentTime / video.duration) * 100;
        setVideoPercentage(percent);
    };





    const formatTime = (seconds?: number) => {
        if (!seconds || Number.isNaN(seconds)) return "0:00";

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        return `${mins}:${String(secs).padStart(2, "0")}`;
    };

    const handleFullscreen = () => {
        videoRef.current?.requestFullscreen();
    };



    useEffect(() => {
        setMediaPortalTarget(document.getElementById("media-player-slot"));
        setVideoPortalTarget(document.getElementById("video-player-slot"));
    }, []);

    const { roomId } = useParams();
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        const fetchSrc = async () => {
            const { data } = await api.get(`/rooms/${roomId}/files/${id}/getDownload`);
            const durl = data.downloadUrl;

            const shouldUseBlob =
                mime?.startsWith("video/") ||
                mime?.startsWith("audio/");

            if (shouldUseBlob) {
                const res = await fetch(durl);

                if (!res.ok) {
                    throw new Error("Failed to fetch media preview");
                }

                const blob = await res.blob();
                objectUrl = URL.createObjectURL(blob);

                if (!cancelled) {
                    setPreviewSrc(objectUrl);
                }
            } else {
                if (!cancelled) {
                    setPreviewSrc(durl);
                }
            }
        };

        fetchSrc();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [roomId, id, mime]);

    return (
        <div className={styles.filePreview}>

            {
                mime?.startsWith("image/") ? (
                    <div className={styles.fpImg}>
                        {previewSrc ? (
                            <img src={previewSrc} alt={name} />
                        ) : (
                            <div className={styles.previewLoader}>
                                <div className={styles.spinner} />
                            </div>
                        )}
                    </div>
                ) : mime.startsWith("video/") ? (
                    <div className={styles.fpVid}>
                        {previewSrc ? (
                            <video
                                ref={videoRef}
                                src={previewSrc}
                                onTimeUpdate={updateVideoProgress}
                                onLoadedMetadata={updateVideoProgress}
                                onEnded={() => {
                                    setVideoPlay(false);
                                    setVideoPercentage(0);
                                }}
                            />
                        ) : (
                            <div className={styles.previewLoader}>
                                <div className={styles.spinner} />
                            </div>
                        )}

                        {videoPortalTarget && previewSrc &&
                            createPortal(
                                <div className={styles.playArea}>
                                    <button onClick={VideoPlayClick}>
                                        {videoPlay ? (
                                            <PauseIcon />
                                        ) : (
                                            <PlayIcon />
                                        )}
                                    </button>
                                    <div className={styles.volumeControl}>
                                        <div className={styles.volumeBtn}>
                                            <VolumeIcon />
                                            <input
                                                className={styles.volumeSlider}
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                defaultValue="1"
                                                style={{ "--progress": "100%" } as React.CSSProperties}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);

                                                    e.currentTarget.style.setProperty("--progress", `${value * 100}%`);

                                                    if (!videoRef.current) return;
                                                    videoRef.current.volume = value;
                                                }}
                                            />
                                        </div>


                                    </div>
                                    <div className={styles.vidPArea}>
                                        <input
                                            className={styles.videoSlider}
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={videoPercentage}
                                            style={{ "--progress": `${videoPercentage}%` } as React.CSSProperties}
                                            onChange={(e) => {
                                                const video = videoRef.current;
                                                if (!video || !video.duration) return;

                                                const percent = Number(e.target.value);
                                                video.currentTime = (percent / 100) * video.duration;
                                                setVideoPercentage(percent);
                                            }}
                                        />


                                        <div style={{ marginTop: '9px' }} />

                                        <div className={styles.controlTwo}>
                                            <p>
                                                {formatTime(videoRef.current?.currentTime)} / {formatTime(videoRef.current?.duration)}
                                            </p>





                                            <div className={styles.fullScreen}
                                                onClick={handleFullscreen}
                                            >
                                                <FullScreenIcon />
                                            </div>
                                        </div>
                                    </div>
                                </div>,
                                videoPortalTarget
                            )
                        }
                    </div>
                ) : mime.startsWith("audio/") ? (
                    <div className={styles.fpAud}>
                        {mediaPortalTarget && previewSrc &&
                            createPortal(
                                <audio controls src={previewSrc} />,
                                mediaPortalTarget
                            )}
                        <SoundIcon />
                    </div>
                ) :
                    (
                        <div className={styles.fpNoPrev}>
                            <img draggable={false} src={`${process.env.PUBLIC_URL}/images/${getFileRefIcon(name)}`} />
                            <p>No Preview Available</p>
                        </div>
                    )
            }

        </div>
    )
}

// : mime === "application/pdf" ? (
//                     <div className={styles.fpPdf}>
//                         <iframe src={previewSrc} title={name} />
//                     </div>
//                 )

export default FilePreview
